import { useEffect, useState } from 'react'
import { nextPosition } from '@domain/list'
import type { ListItem, ShoppingList } from '@domain/list'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Botao } from '@ui/glass/Botao'
import { EscolherLista } from './EscolherLista'
import type { ListaComEstado } from './EscolherLista'

type Props = {
  /** O texto do item, quando ele for criado. O nome do produto ou do grupo. */
  texto: string
  /** A embalagem, quando o botão está na página de um produto. */
  productId?: string
  /** O grupo, quando o botão está na página de uma família. */
  familyId?: string
}

type Estado =
  | { tipo: 'lendo' }
  | { tipo: 'semLista' }
  /** Uma lista só: o botão grava direto, sem perguntar. */
  | { tipo: 'fora'; lista: ShoppingList; itens: ListItem[] }
  | { tipo: 'dentro' }
  /** Duas ou mais: o botão abre a folha, e cada lista sabe se já tem o item. */
  | { tipo: 'escolher'; listas: ListaComEstado[]; itensPorLista: Map<string, ListItem[]> }
  | { tipo: 'gravando' }
  | { tipo: 'falhou' }

/**
 * "Lembrar" — põe este produto ou grupo na lista de compras, de onde ele está.
 *
 * ## Por que ele existe aqui e não só nos Lembretes
 *
 * O caminho até agora era o contrário: abrir Lembretes, digitar o nome, tocar
 * na sugestão. Mas a decisão de comprar de novo nasce **olhando o produto** —
 * vendo que o preço subiu, ou que faz tempo desde a última compra. Obrigar a
 * sair da tela, lembrar o nome e digitá-lo é pedir que a pessoa carregue na
 * cabeça o que já está na frente dela.
 *
 * ## Ele lê UMA VEZ, e não fica ouvindo
 *
 * A decisão 168 reservou `onSnapshot` para onde tempo real é requisito — os
 * Lembretes abertos, com duas pessoas no mercado. Aqui não é: quem abre a
 * página de um produto quer saber se ele está na lista **agora**, e manter duas
 * assinaturas abertas por página de produto seria conexão para dado parado.
 *
 * A porta só oferece leitura ao vivo, então a leitura única é feita com ela:
 * assina, pega o primeiro retrato, cancela. O estado que aparece é o de quando
 * a tela abriu — e se a outra pessoa puser o mesmo item nesse meio-tempo, a
 * gravação duplicaria. É o mesmo risco que a busca dos Lembretes já corre, e o
 * conserto seria o mesmo: nenhum, porque item repetido na lista de compras se
 * resolve apagando um.
 *
 * ## Com DUAS listas ou mais, ele pergunta em qual
 *
 * Ele punha na primeira lista viva — o que era a resposta certa enquanto só
 * havia uma. Com "padrão" e "farmácia" na casa, escolher pela ordem do banco é
 * escolher por acaso, e o remédio ia para o mercado.
 *
 * Com uma lista só ele **não** pergunta: confirmar o óbvio é atrito puro, e o
 * caminho de um toque era o motivo de este botão existir.
 *
 * ## "Já está" passou a ser POR LISTA
 *
 * Com uma lista, "já está" era um estado do botão. Com duas, é uma resposta
 * diferente para cada uma — e some da tela se o botão continuar sendo um
 * carimbo só. Por isso a folha marca lista a lista, e o botão só se declara
 * "Na lista" quando **todas** já têm: é o único caso em que não há o que fazer.
 *
 * ## Já está na lista? O botão diz, e não deixa somar de novo
 *
 * Mesma regra do `LinhaDeSugestao`: desabilitado com o motivo à vista, em vez
 * de escondido. Sumir faria parecer que o botão nunca existiu.
 *
 * **Só conta o que está para comprar.** Item concluído é compra feita, e querer
 * de novo é o caso normal da semana seguinte.
 */
export function BotaoLembrar({ texto, productId, familyId }: Props) {
  const { listRepository } = useFinanceRepositories()
  const [estado, setEstado] = useState<Estado>({ tipo: 'lendo' })
  const [escolhendo, setEscolhendo] = useState(false)

  useEffect(() => {
    let vivo = true
    let pararListas: (() => void) | null = null
    let pararItens: (() => void) | null = null

    /**
     * Cancela **no microtask seguinte**, e não na hora.
     *
     * A assinatura pode entregar o primeiro retrato **de forma síncrona** — é
     * o que o dublê em memória faz —, e aí o callback roda antes de a variável
     * do cancelador existir. A primeira versão fazia `pararListas()` dentro do
     * próprio callback e explodia com `Cannot access 'pararListas' before
     * initialization`, derrubando o componente inteiro no boundary. Adiar um
     * microtask basta: quando ele roda, a atribuição já aconteceu, tanto no
     * caminho síncrono quanto no assíncrono.
     */
    const cancelarDepois = (qual: 'listas' | 'itens') => {
      queueMicrotask(() => {
        if (qual === 'listas') {
          pararListas?.()
          pararListas = null
        } else {
          pararItens?.()
          pararItens = null
        }
      })
    }

    pararListas = listRepository.observeLists(
      (listas: ShoppingList[]) => {
        if (!vivo) return
        const vivas = listas.filter((l) => l.archivedAt === null)
        if (vivas.length === 0) {
          setEstado({ tipo: 'semLista' })
          cancelarDepois('listas')
          return
        }

        // Os itens de TODAS as listas, e não só os da primeira: a resposta
        // "já está" mudou de ser um estado do botão para ser uma por lista.
        const porLista = new Map<string, ListItem[]>()
        const paradas: (() => void)[] = []
        let faltam = vivas.length

        for (const lista of vivas) {
          const parar = listRepository.observeItems(
            lista.id,
            (itens) => {
              if (!vivo || porLista.has(lista.id)) return
              porLista.set(lista.id, itens)
              faltam -= 1
              if (faltam > 0) return

              const comEstado = vivas.map((l) => ({
                lista: l,
                jaEsta: (porLista.get(l.id) ?? []).some(
                  (i) =>
                    !i.completed &&
                    ((productId !== undefined && i.productId === productId) ||
                      (familyId !== undefined && i.familyId === familyId)),
                ),
              }))

              if (comEstado.every((x) => x.jaEsta)) {
                setEstado({ tipo: 'dentro' })
              } else if (comEstado.length === 1 && comEstado[0] !== undefined) {
                const so = comEstado[0]
                setEstado({ tipo: 'fora', lista: so.lista, itens: porLista.get(so.lista.id) ?? [] })
              } else {
                setEstado({ tipo: 'escolher', listas: comEstado, itensPorLista: porLista })
              }
              for (const p of paradas) p()
            },
            (erro: unknown) => {
              console.error('BotaoLembrar: os itens não carregaram', erro)
              if (vivo) setEstado({ tipo: 'falhou' })
            },
          )
          paradas.push(parar)
        }
        pararItens = () => {
          for (const p of paradas) p()
        }
        cancelarDepois('listas')
      },
      (erro: unknown) => {
        console.error('BotaoLembrar: as listas não carregaram', erro)
        if (vivo) setEstado({ tipo: 'falhou' })
      },
    )

    return () => {
      vivo = false
      pararListas?.()
      pararItens?.()
    }
  }, [listRepository, productId, familyId])

  /** Grava o item numa lista. O mesmo caminho, venha do botão ou da folha. */
  function por(listaId: string, itens: ListItem[]): void {
    setEstado({ tipo: 'gravando' })
    void listRepository
      .addItem(listaId, {
        text: texto,
        // Um ou outro, nunca os dois: o vínculo é de grupo **ou** de embalagem,
        // como no caminho da busca dos Lembretes.
        productId: productId ?? null,
        familyId: familyId ?? null,
        quantity: null,
        unit: null,
        note: null,
        dueAt: null,
        position: nextPosition(itens),
      })
      .then(() => {
        setEstado({ tipo: 'dentro' })
      })
      .catch((erro: unknown) => {
        console.error('BotaoLembrar: não deu para adicionar', erro)
        setEstado({ tipo: 'falhou' })
      })
  }

  if (estado.tipo === 'dentro') {
    return (
      // Um ESTADO, e não um botão apagado: o `Botao` desabilitado agora some
      // a 40% (decisão 311), e "Na lista" é informação que precisa se ler. Fica
      // inteiro, na cor do texto secundário — não convida ao toque. Por `style`
      // porque é o que vence as classes do `Botao`; ver o cabeçalho dele.
      <Botao type="button" disabled className="text-xs" style={{ opacity: 1, color: 'var(--text-muted)' }}>
        <span aria-hidden="true">✓</span>
        Na lista
      </Botao>
    )
  }

  const impedido = estado.tipo !== 'fora' && estado.tipo !== 'escolher'

  return (
    <>
      <Botao
        type="button"
        disabled={impedido}
        // O motivo do impedimento é anunciado, e não só desenhado: um botão
        // apagado sem explicação é um botão quebrado para quem não vê a tela.
        aria-label={
          estado.tipo === 'semLista' ? 'Lembrar — não há lista de compras ainda' : `Lembrar ${texto}`
        }
        className="text-xs"
        onClick={() => {
          if (estado.tipo === 'fora') por(estado.lista.id, estado.itens)
          else if (estado.tipo === 'escolher') setEscolhendo(true)
        }}
      >
        <span aria-hidden="true">+</span>
        {estado.tipo === 'falhou' ? 'Não deu' : 'Lembrar'}
      </Botao>

      {escolhendo && estado.tipo === 'escolher' && (
        <EscolherLista
          texto={texto}
          listas={estado.listas}
          gravandoEm={null}
          onEscolher={(lista) => {
            setEscolhendo(false)
            por(lista.id, estado.itensPorLista.get(lista.id) ?? [])
          }}
          onFechar={() => {
            setEscolhendo(false)
          }}
        />
      )}
    </>
  )
}
